import React, { useMemo } from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { i18n } from './changeHistoryMacroI18n.ts';
import {
	getChangeHistoryVersions,
	getSupportedContentError,
	isSupportedContent,
} from './changeHistoryMacroUtils.ts';
import { ChangeHistoryErrorDisplay } from './components/ChangeHistoryErrorDisplay.tsx';
import { ChangeHistoryTable } from './components/ChangeHistoryTable.tsx';
import { LoadingState } from './components/LoadingState.tsx';
import { useChangeHistoryData } from './hooks/useChangeHistoryData.ts';

export type ChangeHistoryMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: Record<string, unknown>;
};

const ChangeHistoryMacroRendererComponent = ({
	macroDefaultProps,
}: ChangeHistoryMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);
	const { data, loading, error, loadNextPage, isLoadingNextPage, loadNextPageError } =
		useChangeHistoryData({ contentId });
	const versions = useMemo(
		() => getChangeHistoryVersions(data?.contentHistory, data?.singleContent?.version),
		[data?.contentHistory, data?.singleContent?.version],
	);

	const renderMacroExperienceFailure = (
		macroError: Error,
		messageDescriptor: MessageDescriptor,
	) => (
		<>
			<MacroExperienceFailure
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
				error={macroError}
			/>
			<ChangeHistoryErrorDisplay error={macroError}>
				<FormattedMessage {...messageDescriptor} />
			</ChangeHistoryErrorDisplay>
		</>
	);

	if (!contentId) {
		const invalidParamsError = new Error(formatMessage(i18n.invalidParams));
		return renderMacroExperienceFailure(invalidParamsError, i18n.invalidParams);
	}

	if (loading && !data) {
		return <LoadingState />;
	}

	if (error) {
		return renderMacroExperienceFailure(error, i18n.error);
	}

	if (!isSupportedContent(data?.singleContent)) {
		const unsupportedContentError = getSupportedContentError(formatMessage);
		return renderMacroExperienceFailure(unsupportedContentError, i18n.unsupportedContent);
	}

	const spaceKey = data?.singleContent?.space?.key ?? macroDefaultProps.spaceKey;

	if (!spaceKey) {
		const dataError = new Error(formatMessage(i18n.error));
		return renderMacroExperienceFailure(dataError, i18n.error);
	}

	return (
		<>
			<Box data-fabric-macro={attributes?.macroId} data-macro-body="" testId="change-history-macro">
				<ChangeHistoryTable
					versions={versions}
					spaceKey={spaceKey}
					contentId={contentId}
					hasNextPage={Boolean(data?.contentHistory?.pageInfo?.hasNextPage)}
					onViewMore={loadNextPage}
					isLoadingNextPage={isLoadingNextPage}
					loadNextPageError={loadNextPageError}
				/>
			</Box>
			<MacroExperienceSuccess
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
			/>
		</>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

export const ChangeHistoryMacroRenderer = (
	props: ChangeHistoryMacroRendererProps,
): React.JSX.Element => (
	<GenericErrorBoundary
		renderOnError={renderErrorFallback}
		attribution={Attribution.PAGE_EXTENSIONS}
	>
		<ChangeHistoryMacroRendererComponent {...props} />
	</GenericErrorBoundary>
);
